import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import { Clock, AlertTriangle, AlertCircle, Megaphone, FileText, FileQuestion, Phone, GitBranch } from 'lucide-react';
import type { DropOffItem, FeatureType } from '@respectly/shared-types';

interface DropOffTableProps {
  items: DropOffItem[];
  isLoading?: boolean;
}

type FilterType = 'all' | FeatureType;

const FEATURE_CONFIG: Record<FeatureType, { label: string; icon: React.ComponentType<any>; color: string }> = {
  campaigns: { label: 'Campaigns', icon: Megaphone, color: '#3b82f6' },
  documents: { label: 'Documents', icon: FileText, color: '#8b5cf6' },
  forms: { label: 'Forms', icon: FileQuestion, color: '#10b981' },
  voice: { label: 'Voice', icon: Phone, color: '#f59e0b' },
  workflows: { label: 'Workflows', icon: GitBranch, color: '#a855f7' },
  conversations: { label: 'Messaging', icon: Megaphone, color: '#06b6d4' },
};

const FILTERS: { key: FilterType; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'documents', label: 'Documents' },
  { key: 'forms', label: 'Forms' },
  { key: 'voice', label: 'Voice' },
  { key: 'workflows', label: 'Workflows' },
];

function formatDuration(ms: number): string {
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h`;
  const mins = Math.floor(ms / (1000 * 60));
  return `${mins}m`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function UrgencyBadge({ ms }: { ms: number }) {
  const hours = ms / (1000 * 60 * 60);
  if (hours > 24) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
        <AlertCircle className="h-2.5 w-2.5" />
        Critical
      </span>
    );
  }
  if (hours > 1) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        <AlertTriangle className="h-2.5 w-2.5" />
        Warning
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
      <Clock className="h-2.5 w-2.5" />
      Recent
    </span>
  );
}

export function DropOffTable({ items, isLoading }: DropOffTableProps) {
  const [filter, setFilter] = useState<FilterType>('all');

  const filtered = useMemo(
    () => (filter === 'all' ? items : items.filter((i) => i.feature === filter)),
    [items, filter]
  );

  if (isLoading) {
    return (
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-red-500/10 dark:bg-red-500/20 flex items-center justify-center">
              <AlertCircle className="h-4 w-4 text-red-500" />
            </div>
            <div>
              <CardTitle className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Incomplete Processes
              </CardTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Started but not completed — sorted by time stuck
              </p>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex items-center gap-1 flex-wrap">
            {FILTERS.map((f) => {
              const count = f.key === 'all' ? items.length : items.filter((i) => i.feature === f.key).length;
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    active
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                  }`}
                >
                  {f.label}
                  {count > 0 && (
                    <span className={`ml-1 ${active ? 'opacity-70' : 'opacity-50'}`}>({count})</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {filtered.length === 0 ? (
          <div className="py-12 text-center">
            <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-3">
              <AlertCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">All clear!</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">No incomplete processes in this period.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/40">
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px]">Account</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px]">Feature</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px]">Item</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px]">Stage Reached</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px]">Started</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px]">Time Stuck</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px]">Urgency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.map((item) => {
                  const fConf = FEATURE_CONFIG[item.feature];
                  const FeatureIcon = fConf?.icon ?? Megaphone;

                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="px-4 py-2.5">
                        <span className="font-medium text-slate-800 dark:text-slate-200 max-w-[120px] truncate block">
                          {item.tenantName}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                          style={{ color: fConf?.color, backgroundColor: `${fConf?.color}18` }}
                        >
                          <FeatureIcon className="h-2.5 w-2.5" />
                          {fConf?.label ?? item.feature}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-slate-700 dark:text-slate-300 max-w-[140px] truncate block" title={item.itemName}>
                          {item.itemName}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-slate-500 dark:text-slate-400 max-w-[140px] truncate block" title={item.stageReached}>
                          {item.stageReached}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-slate-500 dark:text-slate-400">
                        {formatDate(item.startedAt)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className="font-mono font-semibold text-slate-700 dark:text-slate-300">
                          {formatDuration(item.stuckDurationMs)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <UrgencyBadge ms={item.stuckDurationMs} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
